import { useParams, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { MessageCircle, ArrowUpRight, Info, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Link } from "@/i18n/routing";
import { Seo } from "@/components/Seo";
import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { FeatureList, StepCard } from "@/components/Cards";
import { LeadDialog } from "@/components/LeadDialog";
import { ContactChannels } from "@/components/ContactChannels";
import { EmergencyNotice } from "@/components/EmergencyNotice";
import { ImagePlaceholder } from "@/components/ImagePlaceholder";
import { CONTACT, waLink } from "@/data/site";
import { LANDING_SERVICES, getLandingService } from "@/data/landingServices";

const BASE = `https://${CONTACT.domain}`;
const I18N_KEY = { "/doctor-to-hotel-vietnam": "doctorToHotel", "/urgent-medical-support-vietnam": "urgentSupport" };

const ServicePage = ({ slug }) => {
  const { t } = useTranslation();
  const { lang } = useParams();
  const page = getLandingService(slug);
  if (!page) return <Navigate to="/urgent-care" replace />;

  const key = I18N_KEY[page.path];
  const tv = (field, def, opts = {}) => (key ? t(`pages.${key}.${field}`, { defaultValue: def, ...opts }) : def);
  const h1 = tv("h1", page.h1);
  const intro = tv("intro", page.intro);
  const included = tv("included", page.included, { returnObjects: true });
  const limitations = tv("limitations", page.limitations, { returnObjects: true });
  const faqs = tv("faqs", page.faqs, { returnObjects: true });
  const seoTitle = tv("seoTitle", page.seoTitle);
  const metaDescription = tv("metaDescription", page.metaDescription);

  const Icon = page.icon;
  const related = LANDING_SERVICES.filter((s) => s.path !== page.path);

  const serviceSchema = {
    "@context": "https://schema.org", "@type": "Service", name: h1, serviceType: page.serviceType,
    description: metaDescription, areaServed: { "@type": "Country", name: "Vietnam" },
    provider: { "@type": "Organization", name: "Doctors Vietnam", url: BASE, email: CONTACT.email, telephone: `+${CONTACT.whatsappNumber}` },
    url: `${BASE}/${lang}${page.path}`,
  };
  const breadcrumbSchema = {
    "@context": "https://schema.org", "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: t("nav.home"), item: `${BASE}/${lang}` },
      { "@type": "ListItem", position: 2, name: t("nav.urgentCare"), item: `${BASE}/${lang}/urgent-care` },
      { "@type": "ListItem", position: 3, name: h1, item: `${BASE}/${lang}${page.path}` },
    ],
  };
  const faqSchema = {
    "@context": "https://schema.org", "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
  };

  return (
    <>
      <Seo exact title={seoTitle} description={metaDescription} path={page.path} jsonLd={[serviceSchema, breadcrumbSchema, faqSchema]} />
      <PageHero eyebrow={t("nav.urgentCare")} title={h1} subtitle={intro}
        crumbs={[{ label: t("nav.home"), to: "/" }, { label: t("nav.urgentCare"), to: "/urgent-care" }, { label: t(`nav.${key === "doctorToHotel" ? "doctorToHotel" : "urgentCare"}`) }]} />

      <section className="vc-container py-14 sm:py-16">
        <Reveal><EmergencyNotice className="border-2 py-4" /></Reveal>

        <div className="mt-10 grid gap-10 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <Reveal>
              <span className="grid h-14 w-14 place-items-center rounded-2xl bg-mint text-brand"><Icon className="h-7 w-7" /></span>
              <p className="mt-5 text-lg leading-relaxed text-[hsl(var(--vc-ink))]">{intro}</p>
            </Reveal>

            <Reveal className="mt-10">
              <h2 className="text-xl font-extrabold text-[hsl(var(--vc-ink))]">{t("service.included")}</h2>
              <FeatureList items={included} className="mt-4" />
            </Reveal>

            <Reveal className="mt-10">
              <h2 className="text-xl font-extrabold text-[hsl(var(--vc-ink))]">{t("service.howItWorks")}</h2>
              <div className="mt-4 grid gap-4 sm:grid-cols-2">
                <StepCard n="01" icon={Sparkles} title={t("steps.s1t")} text={t("steps.s1x")} />
                <StepCard n="02" icon={Info} title={t("steps.s2t")} text={t("steps.s2x")} />
                <StepCard n="03" icon={Sparkles} title={t("steps.s3t")} text={t("steps.s3x")} />
                <StepCard n="04" icon={Info} title={t("steps.s4t")} text={t("steps.s4x")} />
              </div>
            </Reveal>

            <Reveal className="mt-10">
              <div className="rounded-2xl border border-[hsl(var(--vc-line))] bg-mint p-6">
                <div className="flex items-center gap-2">
                  <span className="grid h-8 w-8 place-items-center rounded-lg bg-brand text-white"><Info className="h-4 w-4" /></span>
                  <h2 className="text-lg font-extrabold text-[hsl(var(--vc-ink))]">{t("service.important")}</h2>
                </div>
                <ul className="mt-4 space-y-2.5">
                  {limitations.map((l) => (
                    <li key={l} className="flex items-start gap-2 text-sm leading-relaxed text-[hsl(var(--vc-slate))]">
                      <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-brand" /> {l}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>

            <Reveal className="mt-10">
              <h2 className="text-xl font-extrabold text-[hsl(var(--vc-ink))]">{t("service.faqTitle")}</h2>
              <Accordion type="single" collapsible className="mt-4" data-testid="service-faq-accordion">
                {faqs.map((f, i) => (
                  <AccordionItem key={i} value={`faq-${i}`} className="border-b border-[hsl(var(--vc-line))]">
                    <AccordionTrigger className="text-left text-base font-bold text-[hsl(var(--vc-ink))] hover:no-underline" data-testid={`service-faq-trigger-${i}`}>{f.q}</AccordionTrigger>
                    <AccordionContent className="text-sm leading-relaxed text-[hsl(var(--vc-slate))]">{f.a}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </Reveal>
          </div>

          <aside>
            <div className="lg:sticky lg:top-24">
              <ImagePlaceholder label={h1} icon={Icon} ratio="aspect-[4/3]" className="mb-5" />
              <div className="rounded-2xl border border-[hsl(var(--vc-line))] bg-white p-6 vc-shadow-card">
                <h3 className="text-lg font-extrabold text-[hsl(var(--vc-ink))]">{t("service.requestTitle")}</h3>
                <p className="mt-1.5 text-sm text-[hsl(var(--vc-slate))]">{t("service.requestText")}</p>
                <LeadDialog title={h1} defaultType="medical" presetService={page.title} source={`service-${page.path.replace(/\//g, "")}`}
                  trigger={<Button className="mt-4 w-full bg-brand py-5 font-bold text-white hover:opacity-90" data-testid="service-request-btn">{t("cta.getMedical")}</Button>} />
                <Button asChild variant="outline" className="mt-3 w-full border-2 border-[hsl(var(--vc-line))] font-bold text-[hsl(var(--vc-ink))] hover:border-brand hover:bg-mint hover:text-brand">
                  <a href={waLink(`Hello Doctors Vietnam, I'm interested in ${page.title}.`)} target="_blank" rel="noopener noreferrer" data-testid="service-whatsapp-btn">
                    <MessageCircle className="mr-2 h-4 w-4" /> {t("cta.whatsappUs")}
                  </a>
                </Button>
                <ContactChannels variant="inline" className="mt-5 flex-col gap-3" />
              </div>
            </div>
          </aside>
        </div>

        <div className="mt-16">
          <h2 className="text-xl font-extrabold text-[hsl(var(--vc-ink))]">{t("service.related")}</h2>
          <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {related.map((r) => (
              <Link key={r.path} to={r.path} data-testid={`related-${r.path.replace(/\//g, "")}`}
                className="group flex items-center justify-between gap-3 rounded-xl border border-[hsl(var(--vc-line))] bg-white p-4 vc-shadow-soft transition hover:border-brand/40 hover:vc-shadow-card">
                <span className="flex items-center gap-3">
                  <span className="grid h-9 w-9 place-items-center rounded-lg bg-mint text-brand"><r.icon className="h-4 w-4" /></span>
                  <span className="text-sm font-semibold text-[hsl(var(--vc-ink))]">{r.title}</span>
                </span>
                <ArrowUpRight className="h-4 w-4 shrink-0 text-brand transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="vc-container pb-16 sm:pb-20">
        <div className="relative overflow-hidden rounded-3xl bg-brand px-6 py-12 text-center text-white vc-shadow-card sm:px-10 sm:py-14">
          <h2 className="text-2xl font-extrabold tracking-tight sm:text-3xl">{t("service.ctaTitle")}</h2>
          <p className="mx-auto mt-3 max-w-xl text-base text-white/85">{t("service.ctaText")}</p>
          <Button asChild className="mt-7 bg-white py-6 text-base font-bold text-brand hover:bg-white/90" data-testid="service-cta-whatsapp">
            <a href={waLink(`Hello Doctors Vietnam, I need help with ${page.title}.`)} target="_blank" rel="noopener noreferrer">
              <MessageCircle className="mr-2 h-5 w-5" /> {t("cta.chatWhatsapp")}
            </a>
          </Button>
          <p className="mt-5 text-xs text-white/70">{CONTACT.whatsappDisplay} · {CONTACT.email}</p>
        </div>
      </section>
    </>
  );
};

export default ServicePage;
